import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Target, Plus, Edit2, Trash2, TrendingUp, Calendar, BarChart3, X,
  DollarSign, FileText, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle, Clock, Users, Download, Filter, RefreshCw, Eye, Save,
  ArrowUp, ArrowDown, PieChart, Layers, Folder
} from 'lucide-react';
import { API_URL } from '../config';

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const MONTHS_SHORT = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

const GestionProjetsKPI = ({ authToken, authUser, employees = [], onNavigate }) => {
  // State
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [kpiData, setKpiData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [signatures, setSignatures] = useState([]);

  // Modals
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [showSubObjModal, setShowSubObjModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingSignature, setEditingSignature] = useState(null);
  const [selectedGoalForTargets, setSelectedGoalForTargets] = useState(null);

  // Filters
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  // KPI Table state
  const [kpiColumns, setKpiColumns] = useState([]);
  const [kpiRows, setKpiRows] = useState([]);
  const [kpiTotals, setKpiTotals] = useState([]);
  const [kpiObjectifs, setKpiObjectifs] = useState([]);
  const [loadingKpiTable, setLoadingKpiTable] = useState(false);
  const [showColModal, setShowColModal] = useState(false);
  const [editingCol, setEditingCol] = useState(null);
  const [colForm, setColForm] = useState({ label: '', col_type: 'text', options_json: '', position: 0 });
  const [pendingCells, setPendingCells] = useState({}); // { 'rowId-colId': value }

  // Forms
  const [goalForm, setGoalForm] = useState({
    label: '', category: 'Commercial', target_amount: '', unit: '€', start_date: '', end_date: ''
  });
  const [signatureForm, setSignatureForm] = useState({
    date_signature: new Date().toISOString().split('T')[0], numero_devis: '', client_name: '',
    type_signature: 'BE', montant_ht: '', montant_ttc: '', goal_id: '', notes: ''
  });
  const [monthlyTargets, setMonthlyTargets] = useState([]);
  const [subObjForm, setSubObjForm] = useState({
    label: '', target_value: '', unit: '€', frequency: 'mensuel', responsible_id: '', kpi_label: '', due_date: ''
  });

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${authToken}`
  }), [authToken]);

  const isPatronOrSM = authUser?.role === 'Patron' || authUser?.role === 'Super Manager';

  // ── Fermeture modales par Escape ──
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (showGoalModal) setShowGoalModal(false);
      else if (showSignatureModal) setShowSignatureModal(false);
      else if (showTargetModal) setShowTargetModal(false);
      else if (showSubObjModal) setShowSubObjModal(false);
      else if (showColModal) setShowColModal(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  // Load projects
  const loadProjects = async () => {
    try {
      const res = await fetch(`${API_URL}/projects`, { headers: { 'Authorization': `Bearer ${authToken}` } });
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  };

  // Load KPI data for selected project
  const loadKpiData = async (projId) => {
    if (!projId) return;
    setLoading(true);
    try {
      const [kpiRes, goalsRes, sigRes] = await Promise.all([
        fetch(`${API_URL}/projects/${projId}/kpi-dashboard`, { headers }),
        fetch(`${API_URL}/projects/${projId}/financial-goals`, { headers }),
        fetch(`${API_URL}/projects/${projId}/signatures`, { headers })
      ]);
      setKpiData(await kpiRes.json());
      setGoals(await goalsRes.json());
      setSignatures(await sigRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if (authToken) loadProjects(); }, [authToken]);
  useEffect(() => { if (selectedProjectId) { loadKpiData(selectedProjectId); loadKpiTable(selectedProjectId); } }, [selectedProjectId]);

  // ---- KPI TABLE ----
  const loadKpiTable = async (projId) => {
    if (!projId) return;
    setLoadingKpiTable(true);
    try {
      const [colRes, rowRes, totRes, objRes] = await Promise.all([
        fetch(`${API_URL}/projects/${projId}/kpi-columns`, { headers }),
        fetch(`${API_URL}/projects/${projId}/kpi-rows`, { headers }),
        fetch(`${API_URL}/projects/${projId}/kpi-totals`, { headers }),
        fetch(`${API_URL}/objectifs?project_id=${projId}`, { headers }),
      ]);
      const [cols, rows, tots, objs] = await Promise.all([colRes.json(), rowRes.json(), totRes.json(), objRes.json()]);
      setKpiColumns(Array.isArray(cols) ? cols : []);
      setKpiRows(Array.isArray(rows) ? rows : []);
      setKpiTotals(Array.isArray(tots) ? tots : []);
      setKpiObjectifs(Array.isArray(objs) ? objs : []);
    } catch (e) { console.error(e); }
    setLoadingKpiTable(false);
  };

  const openColModal = (col = null) => {
    if (col) {
      setEditingCol(col.id);
      setColForm({ label: col.label, col_type: col.col_type, options_json: col.options_json || '', position: col.position });
    } else {
      setEditingCol(null);
      setColForm({ label: '', col_type: 'text', options_json: '', position: kpiColumns.length });
    }
    setShowColModal(true);
  };

  const submitCol = async (e) => {
    e.preventDefault();
    try {
      const url = editingCol ? `${API_URL}/kpi-columns/${editingCol}` : `${API_URL}/projects/${selectedProjectId}/kpi-columns`;
      const method = editingCol ? 'PUT' : 'POST';
      await fetch(url, { method, headers, body: JSON.stringify(colForm) });
      setShowColModal(false);
      loadKpiTable(selectedProjectId);
    } catch { alert('Erreur'); }
  };

  const deleteCol = async (id) => {
    if (!window.confirm('Supprimer cette colonne et toutes ses données ?')) return;
    await fetch(`${API_URL}/kpi-columns/${id}`, { method: 'DELETE', headers });
    loadKpiTable(selectedProjectId);
  };

  const addKpiRow = async () => {
    await fetch(`${API_URL}/projects/${selectedProjectId}/kpi-rows`, {
      method: 'POST', headers, body: JSON.stringify({ row_date: new Date().toISOString().split('T')[0] })
    });
    loadKpiTable(selectedProjectId);
  };

  const deleteKpiRow = async (id) => {
    if (!window.confirm('Supprimer cette ligne ?')) return;
    await fetch(`${API_URL}/kpi-rows/${id}`, { method: 'DELETE', headers });
    loadKpiTable(selectedProjectId);
  };

  const upsertCell = async (rowId, colId, value, colType) => {
    const body = { row_id: rowId, column_id: colId };
    if (colType === 'boolean') body.value_boolean = value ? 1 : 0;
    else if (['number','currency'].includes(colType)) body.value_number = parseFloat(value) || 0;
    else body.value_text = String(value);
    await fetch(`${API_URL}/kpi-cells`, { method: 'PUT', headers, body: JSON.stringify(body) });
    loadKpiTable(selectedProjectId);
  };

  const getCellValue = (row, colId, colType) => {
    const cell = row.cells?.find(c => c.column_id === colId);
    if (!cell) return '';
    if (colType === 'boolean') return cell.value_boolean ? '1' : '0';
    if (['number','currency'].includes(colType)) return cell.value_number != null ? String(cell.value_number) : '';
    return cell.value_text || '';
  };

  // ---- GOAL CRUD ----
  const openGoalModal = (goal = null) => {
    if (goal) {
      setEditingGoal(goal.id);
      setGoalForm({ label: goal.label, category: goal.category, target_amount: goal.target_amount, unit: goal.unit, start_date: goal.start_date || '', end_date: goal.end_date || '' });
    } else {
      setEditingGoal(null);
      const proj = projects.find(p => p.id === selectedProjectId);
      setGoalForm({ label: '', category: 'Commercial', target_amount: '', unit: '€', start_date: proj?.start_date || '', end_date: proj?.end_date || '' });
    }
    setShowGoalModal(true);
  };

  const submitGoal = async (e) => {
    e.preventDefault();
    try {
      const url = editingGoal ? `${API_URL}/financial-goals/${editingGoal}` : `${API_URL}/projects/${selectedProjectId}/financial-goals`;
      const method = editingGoal ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(goalForm) });
      if (!res.ok) { const err = await res.json(); alert(err.error); return; }
      setShowGoalModal(false);
      loadKpiData(selectedProjectId);
    } catch (e) { alert('Erreur'); }
  };

  const deleteGoal = async (id) => {
    if (!window.confirm('Supprimer cet objectif financier et toutes ses données ?')) return;
    await fetch(`${API_URL}/financial-goals/${id}`, { method: 'DELETE', headers });
    loadKpiData(selectedProjectId);
  };

  // ---- SIGNATURE CRUD ----
  const openSignatureModal = (sig = null) => {
    if (sig) {
      setEditingSignature(sig.id);
      setSignatureForm({
        date_signature: sig.date_signature, numero_devis: sig.numero_devis || '', client_name: sig.client_name,
        type_signature: sig.type_signature, montant_ht: sig.montant_ht, montant_ttc: sig.montant_ttc,
        goal_id: sig.goal_id || '', notes: sig.notes || ''
      });
    } else {
      setEditingSignature(null);
      setSignatureForm({
        date_signature: new Date().toISOString().split('T')[0], numero_devis: '', client_name: '',
        type_signature: 'BE', montant_ht: '', montant_ttc: '', goal_id: '', notes: ''
      });
    }
    setShowSignatureModal(true);
  };

  const submitSignature = async (e) => {
    e.preventDefault();
    try {
      const url = editingSignature ? `${API_URL}/signatures/${editingSignature}` : `${API_URL}/projects/${selectedProjectId}/signatures`;
      const method = editingSignature ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(signatureForm) });
      if (!res.ok) { const err = await res.json(); alert(err.error); return; }
      setShowSignatureModal(false);
      loadKpiData(selectedProjectId);
    } catch (e) { alert('Erreur'); }
  };

  const deleteSignature = async (id) => {
    if (!window.confirm('Supprimer cette signature ?')) return;
    await fetch(`${API_URL}/signatures/${id}`, { method: 'DELETE', headers });
    loadKpiData(selectedProjectId);
  };

  // ---- MONTHLY TARGETS ----
  const openTargetModal = (goal) => {
    setSelectedGoalForTargets(goal);
    const startDate = new Date(goal.start_date || projects.find(p => p.id === selectedProjectId)?.start_date || new Date());
    const endDate = new Date(goal.end_date || projects.find(p => p.id === selectedProjectId)?.end_date || new Date(Date.now() + 365*86400000));

    const targets = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

    while (current <= end) {
      const existing = goal.monthly_targets?.find(t => t.year === current.getFullYear() && t.month === current.getMonth() + 1);
      targets.push({
        year: current.getFullYear(),
        month: current.getMonth() + 1,
        target_amount: existing ? existing.target_amount : 0,
        justification: existing?.justification || ''
      });
      current.setMonth(current.getMonth() + 1);
    }

    // Si aucun target défini, répartir équitablement
    if (!goal.monthly_targets?.length && targets.length > 0) {
      const perMonth = Math.round((goal.target_amount / targets.length) * 100) / 100;
      let remaining = goal.target_amount;
      targets.forEach((t, i) => {
        if (i === targets.length - 1) {
          t.target_amount = Math.round(remaining * 100) / 100;
        } else {
          t.target_amount = perMonth;
          remaining -= perMonth;
        }
      });
    }

    setMonthlyTargets(targets);
    setShowTargetModal(true);
  };

  const submitTargets = async () => {
    try {
      const res = await fetch(`${API_URL}/financial-goals/${selectedGoalForTargets.id}/monthly-targets`, {
        method: 'POST', headers, body: JSON.stringify({ targets: monthlyTargets })
      });
      if (!res.ok) { const err = await res.json(); alert(err.error); return; }
      setShowTargetModal(false);
      loadKpiData(selectedProjectId);
    } catch (e) { alert('Erreur'); }
  };

  const monthlyTargetsTotal = useMemo(() =>
    monthlyTargets.reduce((s, t) => s + (parseFloat(t.target_amount) || 0), 0),
    [monthlyTargets]
  );

  // ---- SUB-OBJECTIVES ----
  const openSubObjModal = (goalId) => {
    setSubObjForm({ label: '', target_value: '', unit: '€', frequency: 'mensuel', responsible_id: '', kpi_label: '', due_date: '', goal_id: goalId });
    setShowSubObjModal(true);
  };

  const submitSubObj = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API_URL}/financial-goals/${subObjForm.goal_id}/sub-objectives`, {
        method: 'POST', headers, body: JSON.stringify(subObjForm)
      });
      setShowSubObjModal(false);
      loadKpiData(selectedProjectId);
    } catch (e) { alert('Erreur'); }
  };

  // ---- Filtered signatures ----
  const filteredSignatures = useMemo(() => {
    let list = signatures;
    if (filterMonth) {
      list = list.filter(s => {
        const d = new Date(s.date_signature);
        return d.getMonth() + 1 === parseInt(filterMonth);
      });
    }
    if (filterYear) {
      list = list.filter(s => {
        const d = new Date(s.date_signature);
        return d.getFullYear() === parseInt(filterYear);
      });
    }
    return list;
  }, [signatures, filterMonth, filterYear]);

  const filteredTotal = useMemo(() => ({
    ht: filteredSignatures.reduce((s, sig) => s + (sig.montant_ht || 0), 0),
    ttc: filteredSignatures.reduce((s, sig) => s + (sig.montant_ttc || 0), 0),
    count: filteredSignatures.length
  }), [filteredSignatures]);

  // ---- Grouped by week ----
  const signaturesByWeek = useMemo(() => {
    const weeks = {};
    filteredSignatures.forEach(sig => {
      const d = new Date(sig.date_signature);
      const startOfWeek = new Date(d);
      startOfWeek.setDate(d.getDate() - d.getDay() + 1);
      const key = startOfWeek.toISOString().split('T')[0];
      if (!weeks[key]) weeks[key] = { start: key, signatures: [], total_ht: 0, total_ttc: 0 };
      weeks[key].signatures.push(sig);
      weeks[key].total_ht += sig.montant_ht || 0;
      weeks[key].total_ttc += sig.montant_ttc || 0;
    });
    return Object.values(weeks).sort((a, b) => b.start.localeCompare(a.start));
  }, [filteredSignatures]);

  // Format currency
  const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
  const fmtN = (n) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(n || 0);

  // ============= DASHBOARD GLOBAL (kpi-global + objectifs-alerts) =============
  const [globalKpi, setGlobalKpi] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loadingGlobal, setLoadingGlobal] = useState(false);

  const loadGlobalKpi = useCallback(async () => {
    if (!authToken || !isPatronOrSM) return;
    setLoadingGlobal(true);
    try {
      const [kpiRes, alertsRes] = await Promise.all([
        fetch(`${API_URL}/dashboard/kpi-global`, { headers }),
        fetch(`${API_URL}/objectifs-alerts`, { headers })
      ]);
      const [kpiD, alertsD] = await Promise.all([kpiRes.json(), alertsRes.json()]);
      setGlobalKpi(kpiD);
      setAlerts(Array.isArray(alertsD) ? alertsD : []);
    } catch (e) { console.error(e); }
    setLoadingGlobal(false);
  }, [authToken, isPatronOrSM]);

  useEffect(() => { if (!selectedProjectId) loadGlobalKpi(); }, [selectedProjectId, loadGlobalKpi]);

  // ============= RENDER =============

  // Vue globale + sélecteur de projet
  if (!selectedProjectId) {
    return (
      <div className="min-h-screen bg-gray-950 text-gray-100 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 p-3 rounded-xl">
              <PieChart className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Gestion Projets & KPI</h1>
              <p className="text-text-muted text-sm">Vue globale multi-projets et suivi financier</p>
            </div>
            <button onClick={loadGlobalKpi} className="ml-auto p-2 hover:bg-gray-800 rounded-lg transition">
              <RefreshCw className={`w-5 h-5 text-text-muted ${loadingGlobal ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Dashboard KPI Global (Patron/Super Manager uniquement) */}
          {isPatronOrSM && globalKpi && (
            <div className="mb-8 space-y-6">
              {/* Métriques globales type Monday.com */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 rounded-lg bg-emerald-500/20">
                      <DollarSign className="w-5 h-5 text-emerald-400" />
                    </div>
                    <span className="text-emerald-400 text-xs font-medium">CA Total Signé</span>
                  </div>
                  <div className="text-2xl font-bold text-emerald-300">{fmt(globalKpi.total_signe || 0)}</div>
                  {globalKpi.total_objectif > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs text-text-muted mb-1">
                        <span>vs objectif</span>
                        <span>{Math.round(((globalKpi.total_signe || 0) / globalKpi.total_objectif) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-emerald-500 transition-all"
                          style={{ width: `${Math.min(100, ((globalKpi.total_signe || 0) / globalKpi.total_objectif) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 rounded-lg bg-blue-500/20">
                      <Layers className="w-5 h-5 text-blue-400" />
                    </div>
                    <span className="text-blue-400 text-xs font-medium">Projets Actifs</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-300">{globalKpi.projets_actifs || projects.filter(p => p.status === 'En cours').length}</div>
                  <p className="text-xs text-text-muted mt-1">{projects.length} au total</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <TrendingUp className="w-5 h-5 text-purple-400" />
                    </div>
                    <span className="text-purple-400 text-xs font-medium">Progression Moy.</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-300">{globalKpi.progression_moyenne || 0}%</div>
                  <div className="w-full bg-gray-800 rounded-full h-1.5 mt-2">
                    <div className={`h-1.5 rounded-full transition-all ${(globalKpi.progression_moyenne || 0) >= 70 ? 'bg-purple-500' : 'bg-purple-400'}`}
                      style={{ width: `${Math.min(100, globalKpi.progression_moyenne || 0)}%` }} />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    </div>
                    <span className="text-amber-400 text-xs font-medium">Alertes</span>
                  </div>
                  <div className="text-2xl font-bold text-amber-300">{alerts.length}</div>
                  <p className="text-xs text-text-muted mt-1">objectifs en retard ou critiques</p>
                </div>
              </div>

              {/* Alertes objectifs (type Pipedrive) */}
              {alerts.length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                  <h3 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Objectifs nécessitant une attention
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {alerts.slice(0, 10).map(alert => (
                      <div key={alert.id} className="flex items-center justify-between bg-gray-800/50 rounded-lg px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                            alert.statut === 'en_retard' ? 'bg-red-500' :
                            alert.progression >= 75 ? 'bg-green-500' :
                            alert.progression >= 50 ? 'bg-yellow-500' : 'bg-orange-500'
                          }`} />
                          <div>
                            <p className="text-sm font-medium text-gray-200">{alert.titre}</p>
                            <p className="text-xs text-text-muted">{alert.project_name} {alert.date_fin ? `— Échéance: ${alert.date_fin}` : ''}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-medium text-text-muted">{alert.progression || 0}%</span>
                          <button
                            onClick={() => {
                              const proj = projects.find(p => p.id === alert.project_id);
                              if (proj) setSelectedProjectId(proj.id);
                            }}
                            className="text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1 rounded hover:bg-emerald-500/10 transition"
                          >
                            Voir
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tableau récapitulatif par projet (type Smartsheet) */}
              {globalKpi.projets && globalKpi.projets.length > 0 && (
                <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-800">
                    <h3 className="font-semibold text-gray-200 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-emerald-400" /> Récapitulatif par projet
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-xs text-text-muted uppercase border-b border-gray-800">
                          <th className="text-left px-5 py-3">Projet</th>
                          <th className="text-left px-5 py-3">Chef</th>
                          <th className="text-right px-5 py-3">Objectif</th>
                          <th className="text-right px-5 py-3">Réalisé</th>
                          <th className="text-right px-5 py-3">%</th>
                          <th className="text-center px-5 py-3">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/50">
                        {globalKpi.projets.map(p => (
                          <tr key={p.id} className="hover:bg-gray-800/30 cursor-pointer transition"
                            onClick={() => setSelectedProjectId(p.id)}>
                            <td className="px-5 py-3 font-medium text-gray-200">{p.name}</td>
                            <td className="px-5 py-3 text-sm text-text-muted">{p.chef_nom || '—'}</td>
                            <td className="px-5 py-3 text-sm text-right text-text-muted">{fmt(p.objectif_annuel || 0)}</td>
                            <td className="px-5 py-3 text-sm text-right font-medium text-emerald-400">{fmt(p.realise || 0)}</td>
                            <td className="px-5 py-3 text-right">
                              <span className={`text-sm font-bold ${(p.pourcentage || 0) >= 80 ? 'text-emerald-400' : (p.pourcentage || 0) >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                                {p.pourcentage || 0}%
                              </span>
                            </td>
                            <td className="px-5 py-3 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                p.status === 'En cours' ? 'bg-emerald-500/10 text-emerald-400' :
                                p.status === 'Terminé' ? 'bg-blue-500/10 text-blue-400' :
                                'bg-gray-700 text-text-muted'
                              }`}>
                                {p.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sélecteur de projet (cartes) */}
          <h2 className="text-lg font-semibold text-gray-300 mb-4">Sélectionner un projet</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedProjectId(p.id)}
                className="bg-gray-900 border border-gray-800 rounded-xl p-5 text-left hover:border-emerald-500/50 hover:bg-gray-900/80 transition-all group"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-5 h-5 text-emerald-400 group-hover:text-emerald-300" />
                  <span className="font-semibold text-lg">{p.name}</span>
                </div>
                {p.description && <p className="text-text-muted text-sm mb-3 line-clamp-2">{p.description}</p>}
                <div className="flex items-center gap-4 text-xs text-text-muted">
                  <span className={`px-2 py-1 rounded-full ${p.status === 'En cours' ? 'bg-emerald-500/10 text-emerald-400' : p.status === 'Terminé' ? 'bg-blue-500/10 text-blue-400' : 'bg-gray-700 text-text-muted'}`}>
                    {p.status}
                  </span>
                  {p.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{p.start_date}</span>}
                </div>
              </button>
            ))}

            {projects.length === 0 && (
              <div className="col-span-full text-center py-12 text-text-muted">
                <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun projet trouvé. Crée un projet dans l'onglet "Projets" d'abord.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  // Loading state
  if (loading && !kpiData) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 dark:border-emerald-500 mx-auto"></div>
          <p className="mt-4 text-text-muted">Chargement du tableau de bord KPI...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => { setSelectedProjectId(null); setKpiData(null); }} className="text-text-muted hover:text-white transition-colors">
              <ChevronDown className="w-5 h-5 rotate-90" />
            </button>
            <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 p-2.5 rounded-xl">
              <PieChart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{selectedProject?.name}</h1>
              <p className="text-text-muted text-sm">{selectedProject?.description || 'Suivi financier & KPI'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onNavigate && (
              <>
                <button onClick={() => onNavigate('objectifs')}
                  className="bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-lg text-xs text-gray-300 hover:text-white transition flex items-center gap-1.5">
                  <Target className="w-3.5 h-3.5" /> Objectifs
                </button>
                <button onClick={() => onNavigate('projets')}
                  className="bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-lg text-xs text-gray-300 hover:text-white transition flex items-center gap-1.5">
                  <Folder className="w-3.5 h-3.5" /> Projet
                </button>
              </>
            )}
            <button onClick={() => loadKpiData(selectedProjectId)} className="bg-gray-800 hover:bg-gray-700 p-2 rounded-lg transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* KPI Summary Cards */}
        {kpiData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20 rounded-xl p-4">
              <div className="text-emerald-400 text-xs font-medium mb-1">Objectif Total</div>
              <div className="text-xl font-bold text-emerald-300">{fmt(kpiData.total_target_ht)}</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 rounded-xl p-4">
              <div className="text-cyan-400 text-xs font-medium mb-1">Réalisé HT</div>
              <div className="text-xl font-bold text-cyan-300">{fmt(kpiData.total_realise_ht)}</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-xl p-4">
              <div className="text-purple-400 text-xs font-medium mb-1">Progression</div>
              <div className="text-xl font-bold text-purple-300">{kpiData.pourcentage_global}%</div>
              <div className="w-full bg-gray-800 rounded-full h-1.5 mt-2">
                <div className={`h-1.5 rounded-full transition-all ${kpiData.pourcentage_global >= 80 ? 'bg-emerald-500' : kpiData.pourcentage_global >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(kpiData.pourcentage_global, 100)}%` }} />
              </div>
            </div>
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl p-4">
              <div className="text-amber-400 text-xs font-medium mb-1">Signatures</div>
              <div className="text-xl font-bold text-amber-300">{kpiData.total_signatures}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-900 rounded-xl p-1 overflow-x-auto">
          {[
            { id: 'dashboard', label: 'Tableau de bord KPI', icon: BarChart3 },
            { id: 'signatures', label: 'Signatures', icon: FileText },
            { id: 'objectifs', label: 'Objectifs financiers', icon: Target },
            { id: 'sub_obj', label: 'Sous-objectifs SO', icon: Layers },
            // KPI personnalise masque (routes conservees)
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-emerald-500/20 text-emerald-400' : 'text-text-muted hover:text-gray-200 hover:bg-gray-800'}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ============ TAB: DASHBOARD ============ */}
        {activeTab === 'dashboard' && kpiData && (
          <div className="space-y-6">

            {/* Alertes */}
            {kpiData.goals?.some(g => {
              const now = new Date();
              const end = g.end_date ? new Date(g.end_date) : null;
              const daysLeft = end ? Math.ceil((end - now) / 86400000) : null;
              return (g.pourcentage < 50 && daysLeft && daysLeft < 90) || g.pourcentage < 25;
            }) && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2 text-red-400 font-semibold">
                  <AlertTriangle className="w-5 h-5" /> Alertes
                </div>
                {kpiData.goals.filter(g => g.pourcentage < 50).map(g => (
                  <div key={g.id} className="text-red-300 text-sm py-1">
                    ⚠️ Objectif "{g.label}" : seulement {g.pourcentage}% atteint ({fmt(g.realise_ht)} / {fmt(g.target_amount)})
                  </div>
                ))}
              </div>
            )}

            {/* Objectifs financiers */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Target className="w-5 h-5 text-emerald-400" /> Objectifs Financiers
                </h3>
                {isPatronOrSM && (
                  <button onClick={() => openGoalModal()} className="bg-emerald-500/20 text-emerald-400 px-3 py-1.5 rounded-lg text-sm hover:bg-emerald-500/30 flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Ajouter
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {kpiData.goals?.map(g => (
                  <div key={g.id} className="bg-gray-800/50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{g.label}</span>
                        <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">{g.category}</span>
                      </div>
                      {isPatronOrSM && (
                        <div className="flex gap-1">
                          <button onClick={() => openTargetModal(g)} className="text-blue-400 hover:text-blue-300 p-1" title="Décomposition mensuelle">
                            <Calendar className="w-4 h-4" />
                          </button>
                          <button onClick={() => openSubObjModal(g.id)} className="text-purple-400 hover:text-purple-300 p-1" title="Sous-objectifs">
                            <Layers className="w-4 h-4" />
                          </button>
                          <button onClick={() => openGoalModal(g)} className="text-text-muted hover:text-white p-1">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteGoal(g.id)} className="text-red-400 hover:text-red-300 p-1">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mb-2">
                      <div className="flex-1">
                        <div className="flex justify-between text-xs text-text-muted mb-1">
                          <span>Réalisé: {fmt(g.realise_ht)}</span>
                          <span>Objectif: {fmt(g.target_amount)}</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3">
                          <div className={`h-3 rounded-full transition-all ${g.pourcentage >= 80 ? 'bg-emerald-500' : g.pourcentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(g.pourcentage, 100)}%` }}>
                          </div>
                        </div>
                      </div>
                      <span className={`text-2xl font-bold ${g.pourcentage >= 80 ? 'text-emerald-400' : g.pourcentage >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {g.pourcentage}%
                      </span>
                    </div>
                    <div className="text-xs text-text-muted">{g.nb_signatures} signature(s) • {g.unit}</div>
                  </div>
                ))}

                {(!kpiData.goals || kpiData.goals.length === 0) && (
                  <div className="text-center py-8 text-text-muted">
                    <Target className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>Aucun objectif financier défini.</p>
                    {isPatronOrSM && <p className="text-sm mt-1">Clique sur "Ajouter" pour créer ton premier objectif.</p>}
                  </div>
                )}
              </div>
            </div>

            {/* Graphique mensuel Objectif vs Réalisé */}
            {kpiData.monthly_actuals?.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-cyan-400" /> Suivi Mensuel
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-text-muted border-b border-gray-800">
                        <th className="text-left py-2 px-3">Mois</th>
                        <th className="text-right py-2 px-3">Objectif</th>
                        <th className="text-right py-2 px-3">Réalisé HT</th>
                        <th className="text-right py-2 px-3">Écart</th>
                        <th className="text-right py-2 px-3">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpiData.monthly_actuals.map((ma, i) => {
                        const target = kpiData.monthly_targets
                          ?.filter(t => t.year === parseInt(ma.year) && t.month === parseInt(ma.month))
                          .reduce((sum, t) => sum + (t.target_amount || 0), 0) || 0;
                        const ecart = ma.total_ht - target;
                        return (
                          <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="py-2 px-3 font-medium">{MONTHS_FR[parseInt(ma.month) - 1]} {ma.year}</td>
                            <td className="py-2 px-3 text-right text-text-muted">{fmt(target)}</td>
                            <td className="py-2 px-3 text-right font-semibold text-cyan-300">{fmt(ma.total_ht)}</td>
                            <td className={`py-2 px-3 text-right font-medium ${ecart >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {ecart >= 0 ? '+' : ''}{fmt(ecart)}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {ecart >= 0
                                ? <span className="text-emerald-400 text-xs">✅ Atteint</span>
                                : <span className="text-red-400 text-xs">⚠️ Sous obj.</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Suivi hebdomadaire */}
            {kpiData.weekly_actuals?.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-purple-400" /> Suivi Hebdomadaire
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {kpiData.weekly_actuals.map((w, i) => (
                    <div key={i} className="bg-gray-800/50 rounded-lg p-3 text-center">
                      <div className="text-xs text-text-muted mb-1">Sem. du {new Date(w.week_start).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</div>
                      <div className="text-lg font-bold text-purple-300">{fmt(w.total_ht)}</div>
                      <div className="text-xs text-text-muted">{w.nb_signatures} sig.</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============ TAB: SIGNATURES ============ */}
        {activeTab === 'signatures' && (
          <div className="space-y-4">
            {/* Filters & Actions */}
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-text-muted" />
                <select value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value))}
                  className="bg-gray-800 border border-gray-700 text-sm rounded-lg px-3 py-2 text-gray-200">
                  {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
                  className="bg-gray-800 border border-gray-700 text-sm rounded-lg px-3 py-2 text-gray-200">
                  <option value="">Tous les mois</option>
                  {MONTHS_FR.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-sm text-text-muted">
                  Total HT: <span className="text-cyan-300 font-bold">{fmt(filteredTotal.ht)}</span>
                  &nbsp;|&nbsp;TTC: <span className="text-emerald-300 font-bold">{fmt(filteredTotal.ttc)}</span>
                  &nbsp;|&nbsp;{filteredTotal.count} sig.
                </div>
                {isPatronOrSM && (
                  <button onClick={() => openSignatureModal()}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Ajouter
                  </button>
                )}
              </div>
            </div>

            {/* Signatures grouped by week */}
            {signaturesByWeek.map((week, wi) => (
              <div key={wi} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="bg-gray-800/50 px-4 py-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-300">
                    Semaine du {new Date(week.start).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-cyan-400 font-semibold">HT: {fmt(week.total_ht)}</span>
                    <span className="text-emerald-400 font-semibold">TTC: {fmt(week.total_ttc)}</span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-text-muted text-xs border-b border-gray-800">
                        <th className="text-left py-2 px-3">Date</th>
                        <th className="text-left py-2 px-3">Client</th>
                        <th className="text-right py-2 px-3">Montant HT</th>
                        {isPatronOrSM && <th className="text-center py-2 px-3">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {week.signatures.map(sig => (
                        <tr key={sig.id} className="border-b border-gray-800/30 hover:bg-gray-800/20">
                          <td className="py-2 px-3">{new Date(sig.date_signature).toLocaleDateString('fr-FR')}</td>
                          <td className="py-2 px-3 font-medium">
                            {sig.client_name}
                            {sig.type_signature && <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${sig.type_signature === 'BE' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{sig.type_signature}</span>}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold text-cyan-300">{fmt(sig.montant_ht)}</td>
                          {isPatronOrSM && (
                            <td className="py-2 px-3 text-center">
                              <div className="flex justify-center gap-1">
                                <button onClick={() => openSignatureModal(sig)} className="text-text-muted hover:text-white p-1">
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => deleteSignature(sig.id)} className="text-red-400 hover:text-red-300 p-1">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {filteredSignatures.length === 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center text-text-muted">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune signature pour cette période.</p>
              </div>
            )}
          </div>
        )}

        {/* ============ TAB: OBJECTIFS ============ */}
        {activeTab === 'objectifs' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Objectifs Financiers & Sous-objectifs</h3>
              {isPatronOrSM && (
                <button onClick={() => openGoalModal()} className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1">
                  <Plus className="w-4 h-4" /> Nouvel Objectif
                </button>
              )}
            </div>

            {goals.map(g => (
              <div key={g.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-lg">{g.label}</h4>
                    <div className="flex items-center gap-3 text-sm text-text-muted mt-1">
                      <span className="bg-gray-700 px-2 py-0.5 rounded text-xs">{g.category}</span>
                      <span>Cible: <span className="text-emerald-400 font-medium">{fmt(g.target_amount)}</span></span>
                      <span>{g.unit}</span>
                      {g.start_date && <span>{g.start_date} → {g.end_date}</span>}
                    </div>
                  </div>
                  {isPatronOrSM && (
                    <div className="flex gap-2">
                      <button onClick={() => openTargetModal(g)} className="bg-blue-500/10 text-blue-400 px-3 py-1.5 rounded-lg text-xs hover:bg-blue-500/20 flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" /> Décomposer
                      </button>
                      <button onClick={() => openSubObjModal(g.id)} className="bg-purple-500/10 text-purple-400 px-3 py-1.5 rounded-lg text-xs hover:bg-purple-500/20 flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Sous-obj.
                      </button>
                    </div>
                  )}
                </div>

                {/* Monthly targets display */}
                {g.monthly_targets?.length > 0 && (
                  <div className="mt-3 bg-gray-800/30 rounded-lg p-3">
                    <div className="text-xs font-medium text-text-muted mb-2">Décomposition Mensuelle</div>
                    <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-2">
                      {g.monthly_targets.map((t, i) => (
                        <div key={i} className="text-center bg-gray-800 rounded-lg p-2">
                          <div className="text-[10px] text-text-muted">{MONTHS_SHORT[t.month - 1]}</div>
                          <div className="text-xs font-bold text-gray-200">{fmtN(t.target_amount)}€</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-objectives */}
                {g.sub_objectives?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs font-medium text-text-muted">Sous-objectifs opérationnels</div>
                    {g.sub_objectives.map(so => (
                      <div key={so.id} className="flex items-center justify-between bg-gray-800/30 rounded-lg p-3">
                        <div>
                          <div className="text-sm font-medium">{so.label}</div>
                          <div className="text-xs text-text-muted flex items-center gap-2 mt-0.5">
                            <span>Cible: {fmtN(so.target_value)} {so.unit}</span>
                            <span>•</span>
                            <span>{so.frequency}</span>
                            {so.responsible_name && <><span>•</span><span>{so.responsible_name}</span></>}
                            {so.kpi_label && <><span>•</span><span className="text-purple-400">KPI: {so.kpi_label}</span></>}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold">{fmtN(so.current_value)} / {fmtN(so.target_value)}</div>
                          <div className="w-20 bg-gray-700 rounded-full h-1.5 mt-1">
                            <div className="bg-purple-500 h-1.5 rounded-full" style={{ width: `${Math.min(so.target_value > 0 ? (so.current_value / so.target_value) * 100 : 0, 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {goals.length === 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center text-text-muted">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun objectif financier. Crée-en un pour commencer le suivi.</p>
              </div>
            )}
          </div>
        )}

        {/* ============ TAB: SOUS-OBJECTIFS SO (grille type Smartsheet) ============ */}
        {activeTab === 'sub_obj' && kpiData && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Sous-objectifs Opérationnels (SO)</h3>
            </div>

            {/* Grille par objectif financier */}
            {goals.filter(g => g.sub_objectives?.length > 0).map(g => (
              <div key={g.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-emerald-400">{g.label}</h4>
                    <span className="text-xs text-text-muted">Cible: {fmt(g.target_amount)}</span>
                  </div>
                  {isPatronOrSM && (
                    <button onClick={() => openSubObjModal(g.id)}
                      className="bg-purple-500/10 text-purple-400 px-3 py-1.5 rounded-lg text-xs hover:bg-purple-500/20 flex items-center gap-1">
                      <Plus className="w-3.5 h-3.5" /> Ajouter SO
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-xs text-text-muted uppercase border-b border-gray-800">
                        <th className="text-left px-4 py-2.5">Label</th>
                        <th className="text-right px-4 py-2.5">Cible</th>
                        <th className="text-right px-4 py-2.5">Actuel</th>
                        <th className="text-center px-4 py-2.5">%</th>
                        <th className="text-left px-4 py-2.5">Fréquence</th>
                        <th className="text-left px-4 py-2.5">Responsable</th>
                        <th className="text-left px-4 py-2.5">KPI</th>
                        <th className="text-left px-4 py-2.5">Échéance</th>
                        {isPatronOrSM && <th className="text-center px-4 py-2.5">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/50">
                      {g.sub_objectives.map(so => {
                        const pctVal = so.target_value > 0 ? Math.round((so.current_value || 0) / so.target_value * 100) : 0;
                        return (
                          <tr key={so.id} className="hover:bg-gray-800/30 transition">
                            <td className="px-4 py-2.5 font-medium text-sm">{so.label}</td>
                            <td className="px-4 py-2.5 text-sm text-right text-text-muted">{fmtN(so.target_value)} {so.unit}</td>
                            <td className="px-4 py-2.5 text-sm text-right">
                              {isPatronOrSM ? (
                                <input type="number" defaultValue={so.current_value || 0}
                                  className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-right text-emerald-400"
                                  onBlur={async (e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    if (val !== (so.current_value || 0)) {
                                      await fetch(`${API_URL}/sub-objectives/${so.id}`, {
                                        method: 'PUT', headers,
                                        body: JSON.stringify({ current_value: val })
                                      });
                                      loadKpiData(selectedProjectId);
                                    }
                                  }}
                                />
                              ) : (
                                <span className="text-emerald-400 font-medium">{fmtN(so.current_value || 0)}</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <div className="flex items-center gap-2 justify-center">
                                <div className="w-12 bg-gray-700 rounded-full h-1.5">
                                  <div className={`h-1.5 rounded-full ${pctVal >= 75 ? 'bg-emerald-500' : pctVal >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                    style={{ width: `${Math.min(pctVal, 100)}%` }} />
                                </div>
                                <span className="text-xs font-bold text-gray-300">{pctVal}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-text-muted">{so.frequency}</td>
                            <td className="px-4 py-2.5 text-xs text-text-muted">{so.responsible_name || '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-purple-400">{so.kpi_label || '—'}</td>
                            <td className="px-4 py-2.5 text-xs text-text-muted">{so.due_date || '—'}</td>
                            {isPatronOrSM && (
                              <td className="px-4 py-2.5 text-center">
                                <button onClick={async () => {
                                  if (!window.confirm('Supprimer ce sous-objectif ?')) return;
                                  await fetch(`${API_URL}/sub-objectives/${so.id}`, { method: 'DELETE', headers });
                                  loadKpiData(selectedProjectId);
                                }} className="text-red-400 hover:text-red-300 p-1">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Objectifs sans sous-objectifs */}
            {goals.filter(g => !g.sub_objectives?.length).length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <p className="text-text-muted text-sm mb-3">Objectifs financiers sans sous-objectifs :</p>
                <div className="flex flex-wrap gap-2">
                  {goals.filter(g => !g.sub_objectives?.length).map(g => (
                    <div key={g.id} className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
                      <span className="text-sm">{g.label}</span>
                      {isPatronOrSM && (
                        <button onClick={() => openSubObjModal(g.id)}
                          className="text-purple-400 hover:text-purple-300">
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {goals.length === 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center text-text-muted">
                <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun objectif financier. Créez-en via l'onglet "Objectifs financiers".</p>
              </div>
            )}
          </div>
        )}

        {/* ============ MODALS ============ */}

        {/* Modal: Goal */}
        {showGoalModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-gray-900 border border-gray-700">
              <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-600 rounded-full" /></div>
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 bg-gray-900 shrink-0">
                <h3 className="text-lg font-bold">{editingGoal ? 'Modifier' : 'Nouvel'} Objectif Financier</h3>
                <button onClick={() => setShowGoalModal(false)} className="text-text-muted hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submitGoal} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                  <div>
                    <label className="text-xs text-text-muted">Intitulé *</label>
                    <input value={goalForm.label} onChange={e => setGoalForm({...goalForm, label: e.target.value})} required
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="Ex: CA Salon Colored Ranch" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-text-muted">Montant cible *</label>
                      <input type="number" step="0.01" value={goalForm.target_amount} onChange={e => setGoalForm({...goalForm, target_amount: e.target.value})} required
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="120000" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">Unité</label>
                      <select value={goalForm.unit} onChange={e => setGoalForm({...goalForm, unit: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1">
                        <option value="€">€ (Euros)</option>
                        <option value="clients">Clients</option>
                        <option value="contrats">Contrats</option>
                        <option value="unités">Unités</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-text-muted">Catégorie</label>
                    <select value={goalForm.category} onChange={e => setGoalForm({...goalForm, category: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1">
                      <option value="Commercial">Commercial</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Opérationnel">Opérationnel</option>
                      <option value="Financier">Financier</option>
                      <option value="RH">RH</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-text-muted">Date début</label>
                      <input type="date" value={goalForm.start_date} onChange={e => setGoalForm({...goalForm, start_date: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">Date fin</label>
                      <input type="date" value={goalForm.end_date} onChange={e => setGoalForm({...goalForm, end_date: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" />
                    </div>
                  </div>
                </div>
                <div className="shrink-0 border-t border-gray-700 bg-gray-900 px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                  <button type="button" onClick={() => setShowGoalModal(false)} className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center text-sm text-text-muted hover:text-white border border-gray-700">Annuler</button>
                  <button type="submit" className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-emerald-500 hover:bg-emerald-600 text-white text-sm">
                    {editingGoal ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal: Signature */}
        {showSignatureModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-gray-900 border border-gray-700">
              <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-600 rounded-full" /></div>
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 bg-gray-900 shrink-0">
                <h3 className="text-lg font-bold">{editingSignature ? 'Modifier' : 'Nouvelle'} Signature</h3>
                <button onClick={() => setShowSignatureModal(false)} className="text-text-muted hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submitSignature} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-text-muted">Date *</label>
                      <input type="date" value={signatureForm.date_signature} onChange={e => setSignatureForm({...signatureForm, date_signature: e.target.value})} required
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">N° Devis</label>
                      <input value={signatureForm.numero_devis} onChange={e => setSignatureForm({...signatureForm, numero_devis: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="DEV-20260115-05443" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-text-muted">Nom du client *</label>
                    <input value={signatureForm.client_name} onChange={e => setSignatureForm({...signatureForm, client_name: e.target.value})} required
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="Nom du client" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-text-muted">Type</label>
                      <select value={signatureForm.type_signature} onChange={e => setSignatureForm({...signatureForm, type_signature: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1">
                        <option value="BE">BE (Bon Engagement)</option>
                        <option value="RLE">RLE (Règlement)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">Montant HT</label>
                      <input type="number" step="0.01" value={signatureForm.montant_ht} onChange={e => setSignatureForm({...signatureForm, montant_ht: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">Montant TTC</label>
                      <input type="number" step="0.01" value={signatureForm.montant_ttc} onChange={e => setSignatureForm({...signatureForm, montant_ttc: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="0.00" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-text-muted">Rattacher à un objectif</label>
                    <select value={signatureForm.goal_id} onChange={e => setSignatureForm({...signatureForm, goal_id: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1">
                      <option value="">Aucun (général)</option>
                      {goals.map(g => <option key={g.id} value={g.id}>{g.label} ({fmt(g.target_amount)})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-text-muted">Notes</label>
                    <textarea value={signatureForm.notes} onChange={e => setSignatureForm({...signatureForm, notes: e.target.value})} rows={2}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm mt-1" placeholder="Notes optionnelles..." />
                  </div>
                </div>
                <div className="shrink-0 border-t border-gray-700 bg-gray-900 px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                  <button type="button" onClick={() => setShowSignatureModal(false)} className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center text-sm text-text-muted hover:text-white border border-gray-700">Annuler</button>
                  <button type="submit" className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-emerald-500 hover:bg-emerald-600 text-white text-sm">
                    {editingSignature ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal: Monthly Targets */}
        {showTargetModal && selectedGoalForTargets && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-gray-900 border border-gray-700">
              <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-600 rounded-full" /></div>
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 bg-gray-900 shrink-0">
                <div>
                  <h3 className="text-lg font-bold">Décomposition Mensuelle</h3>
                  <p className="text-sm text-text-muted">{selectedGoalForTargets.label} — Objectif: {fmt(selectedGoalForTargets.target_amount)}</p>
                </div>
                <button onClick={() => setShowTargetModal(false)} className="text-text-muted hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                {/* Cohérence mathématique */}
                <div className={`p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
                  Math.abs(monthlyTargetsTotal - selectedGoalForTargets.target_amount) < 0.01
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : 'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                  {Math.abs(monthlyTargetsTotal - selectedGoalForTargets.target_amount) < 0.01
                    ? <><CheckCircle className="w-4 h-4" /> Cohérence validée : {fmt(monthlyTargetsTotal)} = {fmt(selectedGoalForTargets.target_amount)}</>
                    : <><AlertTriangle className="w-4 h-4" /> Écart : somme mensuels = {fmt(monthlyTargetsTotal)} ≠ objectif {fmt(selectedGoalForTargets.target_amount)} (diff: {fmt(Math.abs(monthlyTargetsTotal - selectedGoalForTargets.target_amount))})</>
                  }
                </div>

                <div className="space-y-2">
                  {monthlyTargets.map((t, i) => (
                    <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 bg-gray-800/30 rounded-lg p-3">
                      <span className="w-24 text-sm font-medium text-gray-300">{MONTHS_FR[t.month - 1]} {t.year}</span>
                      <input type="number" step="0.01" value={t.target_amount}
                        onChange={e => {
                          const updated = [...monthlyTargets];
                          updated[i].target_amount = parseFloat(e.target.value) || 0;
                          setMonthlyTargets(updated);
                        }}
                        className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] text-right" />
                      <span className="hidden sm:block text-text-muted text-sm w-6">€</span>
                      <input value={t.justification || ''} onChange={e => {
                          const updated = [...monthlyTargets];
                          updated[i].justification = e.target.value;
                          setMonthlyTargets(updated);
                        }}
                        className="w-full sm:w-48 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] text-text-muted" placeholder="Justification..." />
                    </div>
                  ))}
                </div>
              </div>

              <div className="shrink-0 border-t border-gray-700 bg-gray-900 px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-between pb-safe">
                <button onClick={() => {
                  const perMonth = Math.round((selectedGoalForTargets.target_amount / monthlyTargets.length) * 100) / 100;
                  let remaining = selectedGoalForTargets.target_amount;
                  const updated = monthlyTargets.map((t, i) => {
                    if (i === monthlyTargets.length - 1) return { ...t, target_amount: Math.round(remaining * 100) / 100 };
                    remaining -= perMonth;
                    return { ...t, target_amount: perMonth };
                  });
                  setMonthlyTargets(updated);
                }} className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center text-blue-400 text-sm hover:text-blue-300 border border-gray-700 sm:border-0">
                  Répartir équitablement
                </button>
                <div className="flex flex-col-reverse sm:flex-row gap-2">
                  <button onClick={() => setShowTargetModal(false)} className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center text-sm text-text-muted hover:text-white border border-gray-700">Annuler</button>
                  <button onClick={submitTargets}
                    disabled={Math.abs(monthlyTargetsTotal - selectedGoalForTargets.target_amount) >= 0.01}
                    className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-700 disabled:text-text-muted text-white text-sm flex items-center justify-center gap-1">
                    <Save className="w-4 h-4" /> Sauvegarder
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Sub-objective */}
        {showSubObjModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden bg-gray-900 border border-gray-700">
              <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-600 rounded-full" /></div>
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 bg-gray-900 shrink-0">
                <h3 className="text-lg font-bold">Nouveau Sous-Objectif</h3>
                <button onClick={() => setShowSubObjModal(false)} className="text-text-muted hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={submitSubObj} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
                  <div>
                    <label className="text-xs text-text-muted">Intitulé *</label>
                    <input value={subObjForm.label} onChange={e => setSubObjForm({...subObjForm, label: e.target.value})} required
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="Ex: Contacter 5 partenaires/semaine" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-text-muted">Valeur cible</label>
                      <input type="number" step="0.01" value={subObjForm.target_value} onChange={e => setSubObjForm({...subObjForm, target_value: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="5" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">Unité</label>
                      <input value={subObjForm.unit} onChange={e => setSubObjForm({...subObjForm, unit: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">Fréquence</label>
                      <select value={subObjForm.frequency} onChange={e => setSubObjForm({...subObjForm, frequency: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1">
                        <option value="quotidien">Quotidien</option>
                        <option value="hebdomadaire">Hebdomadaire</option>
                        <option value="mensuel">Mensuel</option>
                        <option value="trimestriel">Trimestriel</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-text-muted">Responsable</label>
                    <select value={subObjForm.responsible_id} onChange={e => setSubObjForm({...subObjForm, responsible_id: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1">
                      <option value="">Non assigné</option>
                      {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-text-muted">KPI de mesure</label>
                      <input value={subObjForm.kpi_label} onChange={e => setSubObjForm({...subObjForm, kpi_label: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" placeholder="Ex: Nb contacts/semaine" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted">Date limite</label>
                      <input type="date" value={subObjForm.due_date} onChange={e => setSubObjForm({...subObjForm, due_date: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm min-h-[44px] mt-1" />
                    </div>
                  </div>
                </div>
                <div className="shrink-0 border-t border-gray-700 bg-gray-900 px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                  <button type="button" onClick={() => setShowSubObjModal(false)} className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center text-sm text-text-muted hover:text-white border border-gray-700">Annuler</button>
                  <button type="submit" className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 rounded-lg font-medium text-center bg-purple-500 hover:bg-purple-600 text-white text-sm">Créer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* KPI Table tab hidden - backend routes preserved */}

      </div>
    </div>
  );
};

export default GestionProjetsKPI;
